import { Component, HostListener, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { AbstractComponent, Channel, ChannelService, LeadSource, LeadSourceService } from 'lib-trend-core';
import { Validators } from 'ngx-editor';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'app-modal-lead-source',
    // templateUrl: './modal-lead-source.component.html',
    templateUrl: './modal-lead-source.v2.component.html',
    styleUrls: ['./modal-lead-source.component.scss', './modal-lead-source.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalLeadSourceComponent extends AbstractComponent implements OnInit {
  filterControlChannel: FormControl = new FormControl('')

  public channel!: Channel
  public leadSource: LeadSource
  public listChannel!: Channel[]
  public keywords = '';
  public keywordsList: string[] = [];

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ModalLeadSourceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LeadSource,
    private channelService: ChannelService,
    private leadSourceService: LeadSourceService
  ) {
    super(injector)
    this.initializeKeywordsList();
    this.setupForm();
  }

  ngOnInit(): void {
    this.isNew = !this.data;

    this.getListChannel();

    if (!this.isNew) {
      this.populateFormWithData();
    }
  }

  @HostListener('keydown.enter', ['$event'])
  handleEnterKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const target = keyboardEvent.target as HTMLElement;

    if (target.tagName === 'INPUT' && target.getAttribute('formControlName') === 'keywords') {
      keyboardEvent.preventDefault();
      this.processInput();
    }
  }


  private populateFormWithData(): void {
    if (!this.data) return;
    this.leadSource = structuredClone(this.data);
    this.formGroup.patchValue({
      ...this.data,
      channel: this.data.channel?._id ?? null
    });

    this.leadSource.channel = this.data.channel;

    this.initializeKeywordsList();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      channel: [null, Validators.required],
      name: [null, Validators.required],
      description: [null],
      keywords: [[]],
    });

    this.formGroup.valueChanges.subscribe((value) => {
      if (!this.leadSource) {
        this.leadSource = {} as LeadSource;
      }
      Object.assign(this.leadSource, value);
    });
  }

  private getListChannel(): void {
    this.channelService.getList().pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: Channel[]) => {
          this.listChannel = response;
        },
        error: (error) => this.alertService.error(error),
      })
  }

  onSelectChannel(event: MatSelectChange): void {
    this.formGroup.get('channel').setValue(event.source.value);
    this.channel = event.source.value;
  }

  private processInput(): void {
    const input = this.keywords.trim().replace(/,+$/, '');

    if (input) {
      this.addNewWords(input);
      this.keywords = '';
    }
  }

  private addNewWords(input: string): void {
    if (!Array.isArray(this.keywordsList)) {
      this.keywordsList = [];
    }

    const normalizedKeywords = this.keywordsList.map(w =>
      w.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    );

    const newWords = input
      .split(',')
      .map(word => word.trim())
      .filter(word => !!word)
      .map(word => ({
        original: word,
        normalized: word.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      }))
      .filter(({ normalized, original }) => {
        const isDuplicate = normalizedKeywords.includes(normalized);
        if (isDuplicate) {
          this.alertService.error(this.t('leadSource.modal.keywordDuplicate', { word: original }));
        }
        return !isDuplicate;
      })
      .map(({ original }) => original);

    if (newWords.length > 0) {
      this.keywordsList = [...this.keywordsList, ...newWords];
      this.formGroup.get('keywords')?.setValue(this.keywordsList);
    }
  }


  onInputChange(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    this.keywords = inputElement.value ?? '';

    if (event.key === 'Enter' || event.key === ',' || this.keywords.trim().endsWith(',')) {
      if (event.key === ',') {
        event.preventDefault();
      }
      this.processInput();
      inputElement.value = '';
    }
  }

  removeKeyword(word: string): void {
    this.keywordsList = this.keywordsList.filter((w) => w !== word);
  }

  save(): void {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    };
    this.formGroup.patchValue({ keywords: this.keywordsList });
    const saveObservable = this.isNew
      ? this.leadSourceService.create(this.leadSource)
      : this.leadSourceService.update(this.leadSource._id, this.leadSource);

    saveObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: LeadSource) => {
        this.alertService.success(this.t('leadSource.modal.saveSuccess'));
        this.dialogRef.close(response);
      },
      error: (error) => {
        this.alertService.error(error.error.message);
        this.loading = false;
      },
      complete: () => { this.loading = false }
    })
  }

  close(): void {
    this.dialogRef.close();
  }

  private initializeKeywordsList(): void {
    this.keywordsList = Array.isArray(this.data?.keywords)
      ? [
        ...new Set(
          (this.data.keywords as string[])
            .filter((word): word is string => typeof word === 'string')
            .map((word: string) =>
              word
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim()
                .toLowerCase()
            )
            .filter(word => word)
        ),
      ]
      : [];
  }
}
